import React, { useState, useCallback, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
    Star, Copy, Check, AlertCircle, Trash2, Plus, Edit2, X,
    Mail, MessageSquare, RotateCcw, Save
} from 'lucide-react';
import {
    GlassCard, GlassButton, GlassTabs, GlassSelect, GlassInput,
    GlassBadge, GlassModal, ModuleHeader
} from '../ui';
import {
    getReviewTemplates, createReviewTemplate, updateReviewTemplate,
    deleteReviewTemplate, generateReviewTemplate,
    generateReviewResponse, bulkGenerateReviewResponses,
    getSavedReviewResponses, saveReviewResponse, deleteSavedReviewResponse,
    getBrandGuides, BrandGuide, getProjects, Project,
} from '../../services/api';
import './Reviews.css';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

const TABS = [
    { id: 'request-templates', label: 'Review Requests' },
    { id: 'response-gen', label: 'Response Generator' },
    { id: 'bulk', label: 'Bulk Responses' },
    { id: 'saved', label: 'Saved Responses' },
];

const CHANNEL_OPTIONS = [
    { value: 'email', label: 'Email' },
    { value: 'sms', label: 'SMS' },
];

const TRIGGER_OPTIONS = [
    { value: 'post_purchase', label: 'After Purchase' },
    { value: 'post_service', label: 'After Service' },
    { value: 'manual', label: 'Manual Send' },
];

const PLATFORM_OPTIONS = [
    { value: 'google', label: 'Google' },
    { value: 'facebook', label: 'Facebook' },
    { value: 'other', label: 'Other' },
];

const TRIGGER_LABELS: Record<string, string> = {
    post_purchase: 'After Purchase',
    post_service: 'After Service',
    manual: 'Manual',
};

const LoadingState: React.FC<{ message?: string }> = ({ message = 'Loading...' }) => (
    <div className="rv-loading">
        <div className="rv-spinner" />
        <span>{message}</span>
    </div>
);

const ErrorState: React.FC<{ message: string }> = ({ message }) => (
    <div className="rv-error">
        <AlertCircle size={16} />
        <span>{message}</span>
    </div>
);

const CopyButton: React.FC<{ text: string }> = ({ text }) => {
    const [copied, setCopied] = useState(false);
    const handleCopy = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch { /* ignore */ }
    }, [text]);
    return (
        <button className={`rv-copy-btn ${copied ? 'copied' : ''}`} onClick={handleCopy}>
            {copied ? <Check size={12} /> : <Copy size={12} />}
            {copied ? 'Copied' : 'Copy'}
        </button>
    );
};

const StarRating: React.FC<{ value: number; onChange: (v: number) => void }> = ({ value, onChange }) => (
    <div className="rv-star-group">
        {[1, 2, 3, 4, 5].map(n => (
            <button
                key={n}
                className={`rv-star ${n <= value ? 'filled' : 'empty'}`}
                onClick={() => onChange(n)}
                type="button"
                title={`${n} star${n !== 1 ? 's' : ''}`}
            >
                <Star size={22} />
            </button>
        ))}
    </div>
);

const RatingBadge: React.FC<{ rating: number }> = ({ rating }) => {
    const variant = rating >= 4 ? 'success' : rating === 3 ? 'warning' : 'danger';
    return <GlassBadge variant={variant} size="sm">{rating} ★</GlassBadge>;
};

// ---------------------------------------------------------------------------
// Shared Brand Guide Selector
// ---------------------------------------------------------------------------

const BrandGuideSelector: React.FC<{
    value: string;
    guides: BrandGuide[];
    onChange: (id: string) => void;
}> = ({ value, guides, onChange }) => {
    const options = [
        { value: '', label: 'No brand guide' },
        ...guides.map(g => ({ value: g.brand_guide_id, label: g.brand_guide_name })),
    ];
    return (
        <div className="rv-form-group">
            <label>Brand Guide (optional)</label>
            <GlassSelect options={options} value={value} onChange={onChange} placeholder="Select brand guide..." />
        </div>
    );
};

// ---------------------------------------------------------------------------
// Tab 1: Review Request Templates
// ---------------------------------------------------------------------------

const RequestTemplatesTab: React.FC<{ brandGuides: BrandGuide[]; projects: Project[] }> = ({ brandGuides, projects }) => {
    const [templates, setTemplates] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedProject, setSelectedProject] = useState('');

    // Generate modal state
    const [showGenModal, setShowGenModal] = useState(false);
    const [genBusinessType, setGenBusinessType] = useState('');
    const [genChannel, setGenChannel] = useState('email');
    const [genTrigger, setGenTrigger] = useState('post_purchase');
    const [genBrandGuide, setGenBrandGuide] = useState('');
    const [genLoading, setGenLoading] = useState(false);
    const [genResult, setGenResult] = useState<{ subject: string; body: string } | null>(null);

    // Manual / Edit modal state
    const [showFormModal, setShowFormModal] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState<any | null>(null);
    const [formName, setFormName] = useState('');
    const [formChannel, setFormChannel] = useState('email');
    const [formSubject, setFormSubject] = useState('');
    const [formBody, setFormBody] = useState('');
    const [formTrigger, setFormTrigger] = useState('post_purchase');
    const [formSaving, setFormSaving] = useState(false);

    const projectOptions = [
        { value: '', label: 'Select a project...' },
        ...projects.map(p => ({ value: p.project_id, label: p.name })),
    ];

    const loadTemplates = useCallback(async (projectId: string) => {
        if (!projectId) { setTemplates([]); return; }
        setIsLoading(true);
        setError(null);
        try {
            const data = await getReviewTemplates(projectId);
            setTemplates(data);
        } catch (e: any) {
            setError(getApiError(e, 'Failed to load templates'));
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadTemplates(selectedProject);
    }, [selectedProject, loadTemplates]);

    const openEditModal = (template: any) => {
        setEditingTemplate(template);
        setFormName(template.name);
        setFormChannel(template.channel);
        setFormSubject(template.subject || '');
        setFormBody(template.body);
        setFormTrigger(template.trigger);
        setShowFormModal(true);
    };

    const openNewModal = () => {
        setEditingTemplate(null);
        setFormName('');
        setFormChannel('email');
        setFormSubject('');
        setFormBody('');
        setFormTrigger('post_purchase');
        setShowFormModal(true);
    };

    const handleSaveForm = async () => {
        if (!formName.trim() || !formBody.trim() || !selectedProject) return;
        setFormSaving(true);
        try {
            if (editingTemplate) {
                await updateReviewTemplate(editingTemplate.template_id, {
                    name: formName.trim(),
                    channel: formChannel,
                    subject: formChannel === 'email' ? formSubject.trim() : undefined,
                    body: formBody.trim(),
                    trigger: formTrigger,
                });
            } else {
                await createReviewTemplate({
                    project_id: selectedProject,
                    name: formName.trim(),
                    channel: formChannel,
                    subject: formChannel === 'email' ? formSubject.trim() : undefined,
                    body: formBody.trim(),
                    trigger: formTrigger,
                });
            }
            setShowFormModal(false);
            loadTemplates(selectedProject);
        } catch (e: any) {
            setError(getApiError(e, 'Failed to save template'));
        } finally {
            setFormSaving(false);
        }
    };

    const handleDelete = async (templateId: string) => {
        if (!window.confirm('Delete this template?')) return;
        try {
            await deleteReviewTemplate(templateId);
            setTemplates(prev => prev.filter(t => t.template_id !== templateId));
        } catch (e: any) {
            setError(getApiError(e, 'Failed to delete template'));
        }
    };

    const handleGenerate = async () => {
        if (!genBusinessType.trim()) return;
        setGenLoading(true);
        setGenResult(null);
        try {
            const result = await generateReviewTemplate({
                business_type: genBusinessType.trim(),
                channel: genChannel,
                trigger: genTrigger,
                brand_guide_id: genBrandGuide || undefined,
            });
            setGenResult(result);
        } catch (e: any) {
            setError(getApiError(e, 'Generation failed'));
        } finally {
            setGenLoading(false);
        }
    };

    const handleSaveGenerated = () => {
        if (!genResult || !selectedProject) return;
        setFormName(`${genChannel === 'email' ? 'Email' : 'SMS'} - ${TRIGGER_LABELS[genTrigger] || genTrigger}`);
        setFormChannel(genChannel);
        setFormSubject(genResult.subject || '');
        setFormBody(genResult.body);
        setFormTrigger(genTrigger);
        setEditingTemplate(null);
        setShowGenModal(false);
        setShowFormModal(true);
    };

    return (
        <>
            <GlassCard className="rv-form-card">
                <div className="rv-section-header">
                    <h3>Review Request Templates</h3>
                    <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                        <GlassButton variant="secondary" size="sm" onClick={() => setShowGenModal(true)} icon={<RotateCcw size={16} />}>
                            Generate Template
                        </GlassButton>
                        <GlassButton variant="primary" size="sm" onClick={openNewModal} disabled={!selectedProject} icon={<Plus size={16} />}>
                            New Template
                        </GlassButton>
                    </div>
                </div>

                <div className="rv-form-group">
                    <label>Project</label>
                    <GlassSelect options={projectOptions} value={selectedProject} onChange={setSelectedProject} placeholder="Select a project..." />
                </div>

                {error && <ErrorState message={error} />}
                {isLoading && <LoadingState message="Loading templates..." />}

                {!isLoading && selectedProject && templates.length === 0 && (
                    <div className="rv-empty">No templates yet. Generate one with AI or create manually.</div>
                )}

                {!isLoading && templates.length > 0 && (
                    <div className="rv-template-list">
                        {templates.map(t => (
                            <div key={t.template_id} className="rv-template-item">
                                <div className="rv-template-info">
                                    <span className="rv-template-name">{t.name}</span>
                                    <div className="rv-template-meta">
                                        <GlassBadge variant="info" size="sm">
                                            {t.channel === 'email' ? <Mail size={12} /> : <MessageSquare size={12} />}
                                            {' '}{t.channel.toUpperCase()}
                                        </GlassBadge>
                                        <GlassBadge variant="default" size="sm">{TRIGGER_LABELS[t.trigger] || t.trigger}</GlassBadge>
                                    </div>
                                    {t.body && (
                                        <span className="rv-template-body-preview">{t.body.slice(0, 80)}...</span>
                                    )}
                                </div>
                                <div className="rv-template-actions">
                                    <GlassButton variant="ghost" size="sm" icon={<Copy size={14} />} onClick={() => navigator.clipboard.writeText(t.body)} title="Copy body" />
                                    <GlassButton variant="ghost" size="sm" icon={<Edit2 size={14} />} onClick={() => openEditModal(t)} title="Edit" />
                                    <GlassButton variant="ghost" size="sm" icon={<Trash2 size={14} />} onClick={() => handleDelete(t.template_id)} title="Delete" />
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </GlassCard>

            {/* Generate Template Modal */}
            <GlassModal
                isOpen={showGenModal}
                onClose={() => { setShowGenModal(false); setGenResult(null); }}
                title="Generate Review Request Template"
                size="md"
                footer={
                    <>
                        <GlassButton variant="secondary" onClick={() => { setShowGenModal(false); setGenResult(null); }}>Cancel</GlassButton>
                        {!genResult ? (
                            <GlassButton variant="primary" onClick={handleGenerate} loading={genLoading} disabled={!genBusinessType.trim() || genLoading}>
                                Generate
                            </GlassButton>
                        ) : (
                            <GlassButton variant="primary" onClick={handleSaveGenerated} disabled={!selectedProject}>
                                {selectedProject ? 'Use This Template' : 'Select a project first'}
                            </GlassButton>
                        )}
                    </>
                }
            >
                <div className="rv-modal-body">
                    <GlassInput label="Business Type" value={genBusinessType} onChange={setGenBusinessType} placeholder="e.g. dental clinic, car dealership, cafe..." />
                    <div className="rv-form-row">
                        <GlassSelect label="Channel" options={CHANNEL_OPTIONS} value={genChannel} onChange={setGenChannel} />
                        <GlassSelect label="Trigger" options={TRIGGER_OPTIONS} value={genTrigger} onChange={setGenTrigger} />
                    </div>
                    <BrandGuideSelector value={genBrandGuide} guides={brandGuides} onChange={setGenBrandGuide} />
                    {genLoading && <LoadingState message="Generating template with AI..." />}
                    {genResult && (
                        <div className="rv-generated-preview">
                            {genResult.subject && (
                                <>
                                    <h4>Subject</h4>
                                    <p style={{ marginBottom: 'var(--space-3)' }}>{genResult.subject}</p>
                                </>
                            )}
                            <h4>Body</h4>
                            <p>{genResult.body}</p>
                        </div>
                    )}
                </div>
            </GlassModal>

            {/* New / Edit Template Modal */}
            <GlassModal
                isOpen={showFormModal}
                onClose={() => setShowFormModal(false)}
                title={editingTemplate ? 'Edit Template' : 'New Template'}
                size="md"
                footer={
                    <>
                        <GlassButton variant="secondary" onClick={() => setShowFormModal(false)}>Cancel</GlassButton>
                        <GlassButton variant="primary" onClick={handleSaveForm} loading={formSaving} disabled={!formName.trim() || !formBody.trim()}>
                            {editingTemplate ? 'Save Changes' : 'Create Template'}
                        </GlassButton>
                    </>
                }
            >
                <div className="rv-modal-body">
                    <GlassInput label="Template Name *" value={formName} onChange={setFormName} placeholder="e.g. Post-service email" />
                    <div className="rv-form-row">
                        <GlassSelect label="Channel" options={CHANNEL_OPTIONS} value={formChannel} onChange={setFormChannel} />
                        <GlassSelect label="Trigger" options={TRIGGER_OPTIONS} value={formTrigger} onChange={setFormTrigger} />
                    </div>
                    {formChannel === 'email' && (
                        <GlassInput label="Subject Line" value={formSubject} onChange={setFormSubject} placeholder="e.g. How did we do, {{customer_name}}?" />
                    )}
                    <div className="rv-form-group">
                        <label>Body *</label>
                        <textarea
                            className="rv-textarea tall"
                            value={formBody}
                            onChange={e => setFormBody(e.target.value)}
                            placeholder="Use {{customer_name}}, {{business_name}}, {{review_link}} as placeholders"
                        />
                    </div>
                </div>
            </GlassModal>
        </>
    );
};

// ---------------------------------------------------------------------------
// Tab 2: Response Generator
// ---------------------------------------------------------------------------

const ResponseGeneratorTab: React.FC<{ brandGuides: BrandGuide[]; projects: Project[] }> = ({ brandGuides, projects }) => {
    const [reviewText, setReviewText] = useState('');
    const [rating, setRating] = useState(0);
    const [platform, setPlatform] = useState('google');
    const [businessName, setBusinessName] = useState('');
    const [brandGuideId, setBrandGuideId] = useState('');
    const [selectedProject, setSelectedProject] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<{ response_text: string; tone: string; key_points: string[] } | null>(null);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    const projectOptions = [
        { value: '', label: 'Select project to save...' },
        ...projects.map(p => ({ value: p.project_id, label: p.name })),
    ];

    const handleGenerate = async () => {
        if (!reviewText.trim()) { setError('Review text is required.'); return; }
        if (!rating) { setError('Please select a star rating.'); return; }
        if (!businessName.trim()) { setError('Business name is required.'); return; }
        setError(null);
        setLoading(true);
        setResult(null);
        setSaved(false);
        try {
            const data = await generateReviewResponse({
                review_text: reviewText.trim(),
                rating,
                platform,
                business_name: businessName.trim(),
                brand_guide_id: brandGuideId || undefined,
            });
            setResult(data);
        } catch (e: any) {
            setError(getApiError(e, 'Generation failed. Please try again.'));
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!result || !selectedProject) return;
        setSaving(true);
        try {
            await saveReviewResponse({
                project_id: selectedProject,
                review_text: reviewText.trim(),
                rating,
                platform,
                response_text: result.response_text,
            });
            setSaved(true);
        } catch (e: any) {
            setError(getApiError(e, 'Failed to save response'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <GlassCard className="rv-form-card">
                <div className="rv-form-group">
                    <label>Review Text *</label>
                    <textarea
                        className="rv-textarea tall"
                        value={reviewText}
                        onChange={e => setReviewText(e.target.value)}
                        placeholder="Paste the customer review here..."
                    />
                </div>
                <div className="rv-form-row">
                    <div className="rv-form-group">
                        <label>Star Rating *</label>
                        <StarRating value={rating} onChange={setRating} />
                    </div>
                    <GlassSelect label="Platform" options={PLATFORM_OPTIONS} value={platform} onChange={setPlatform} />
                </div>
                <div className="rv-form-row">
                    <GlassInput label="Business Name *" value={businessName} onChange={setBusinessName} placeholder="Your business name" />
                    <BrandGuideSelector value={brandGuideId} guides={brandGuides} onChange={setBrandGuideId} />
                </div>
                {error && <ErrorState message={error} />}
                <div className="rv-form-actions">
                    <GlassButton variant="primary" onClick={handleGenerate} disabled={loading} icon={<Star size={16} />}>
                        {loading ? 'Generating...' : 'Generate Response'}
                    </GlassButton>
                </div>
                {loading && <LoadingState message="Crafting a personalised response..." />}
            </GlassCard>

            {result && !loading && (
                <GlassCard className="rv-response-card">
                    <div className="rv-response-header">
                        <h3>Generated Response</h3>
                        <CopyButton text={result.response_text} />
                    </div>
                    <div className="rv-response-meta">
                        <span className="rv-tone-label">Tone:</span>
                        <GlassBadge variant="info" size="sm">{result.tone}</GlassBadge>
                        <RatingBadge rating={rating} />
                    </div>
                    <div className="rv-response-text">{result.response_text}</div>
                    {result.key_points && result.key_points.length > 0 && (
                        <>
                            <div style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-semibold)', color: 'var(--text-secondary)', marginBottom: 'var(--space-2)' }}>
                                Key points addressed:
                            </div>
                            <ul className="rv-key-points">
                                {result.key_points.map((kp, i) => <li key={i}>{kp}</li>)}
                            </ul>
                        </>
                    )}
                    <div className="rv-response-actions">
                        <GlassSelect options={projectOptions} value={selectedProject} onChange={setSelectedProject} placeholder="Save to project..." />
                        <GlassButton
                            variant="primary"
                            onClick={handleSave}
                            loading={saving}
                            disabled={!selectedProject || saving || saved}
                            icon={<Save size={16} />}
                        >
                            {saved ? 'Saved!' : 'Save Response'}
                        </GlassButton>
                    </div>
                </GlassCard>
            )}
        </>
    );
};

// ---------------------------------------------------------------------------
// Tab 3: Bulk Responses
// ---------------------------------------------------------------------------

interface BulkReviewEntry {
    id: string;
    review_text: string;
    rating: number;
    platform: string;
    response_text?: string;
    tone?: string;
}

const BulkResponsesTab: React.FC<{ brandGuides: BrandGuide[] }> = ({ brandGuides }) => {
    const [entries, setEntries] = useState<BulkReviewEntry[]>([
        { id: '1', review_text: '', rating: 5, platform: 'google' },
    ]);
    const [businessName, setBusinessName] = useState('');
    const [brandGuideId, setBrandGuideId] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [results, setResults] = useState<any[]>([]);
    const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

    const addRow = () => setEntries(prev => [...prev, { id: Date.now().toString(), review_text: '', rating: 5, platform: 'google' }]);
    const removeRow = (id: string) => setEntries(prev => prev.filter(e => e.id !== id));
    const updateEntry = (id: string, field: keyof BulkReviewEntry, value: any) =>
        setEntries(prev => prev.map(e => e.id === id ? { ...e, [field]: value } : e));

    const handleGenerate = async () => {
        const valid = entries.filter(e => e.review_text.trim() && e.rating > 0);
        if (!valid.length) { setError('Add at least one review with text and rating.'); return; }
        if (!businessName.trim()) { setError('Business name is required.'); return; }
        setError(null);
        setLoading(true);
        setResults([]);
        try {
            const data = await bulkGenerateReviewResponses({
                reviews: valid.map(e => ({ review_text: e.review_text.trim(), rating: e.rating, platform: e.platform })),
                business_name: businessName.trim(),
                brand_guide_id: brandGuideId || undefined,
            });
            setResults(data.responses || []);
        } catch (e: any) {
            setError(getApiError(e, 'Bulk generation failed. Please try again.'));
        } finally {
            setLoading(false);
        }
    };

    const toggleExpand = (i: number) => setExpandedRows(prev => {
        const next = new Set(prev);
        if (next.has(i)) next.delete(i); else next.add(i);
        return next;
    });

    return (
        <>
            <GlassCard className="rv-form-card">
                <div className="rv-section-header">
                    <h3>Bulk Review Responses</h3>
                    <GlassButton variant="secondary" size="sm" onClick={addRow} icon={<Plus size={16} />}>Add Review</GlassButton>
                </div>

                {entries.map((entry, idx) => (
                    <div key={entry.id} className="rv-bulk-row">
                        <div>
                            <label style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>
                                Review #{idx + 1}
                            </label>
                            <textarea
                                className="rv-textarea"
                                style={{ minHeight: 72 }}
                                value={entry.review_text}
                                onChange={e => updateEntry(entry.id, 'review_text', e.target.value)}
                                placeholder="Paste review text..."
                            />
                        </div>
                        <div>
                            <label style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', marginBottom: 4, display: 'block' }}>Rating</label>
                            <StarRating value={entry.rating} onChange={v => updateEntry(entry.id, 'rating', v)} />
                        </div>
                        <div>
                            <GlassSelect
                                label="Platform"
                                options={PLATFORM_OPTIONS}
                                value={entry.platform}
                                onChange={v => updateEntry(entry.id, 'platform', v)}
                            />
                        </div>
                        <div style={{ paddingTop: 24 }}>
                            {entries.length > 1 && (
                                <GlassButton variant="ghost" size="sm" icon={<X size={14} />} onClick={() => removeRow(entry.id)} />
                            )}
                        </div>
                    </div>
                ))}

                <div style={{ marginTop: 'var(--space-4)' }}>
                    <GlassInput label="Business Name *" value={businessName} onChange={setBusinessName} placeholder="Your business name" />
                </div>
                <div style={{ marginTop: 'var(--space-3)' }}>
                    <BrandGuideSelector value={brandGuideId} guides={brandGuides} onChange={setBrandGuideId} />
                </div>

                {error && <ErrorState message={error} />}
                <div className="rv-form-actions">
                    <GlassButton variant="primary" onClick={handleGenerate} disabled={loading} icon={<RotateCcw size={16} />}>
                        {loading ? 'Generating All...' : 'Generate All Responses'}
                    </GlassButton>
                </div>
                {loading && <LoadingState message="Generating responses for all reviews..." />}
            </GlassCard>

            {results.length > 0 && !loading && (
                <GlassCard>
                    <h3 style={{ margin: '0 0 var(--space-4) 0', fontSize: 'var(--text-lg)', fontWeight: 'var(--font-semibold)', color: 'var(--text)' }}>
                        Generated Responses
                    </h3>
                    <table className="rv-bulk-results-table">
                        <thead>
                            <tr>
                                <th>Review</th>
                                <th>Rating</th>
                                <th>Tone</th>
                                <th>Response</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {results.map((r, i) => (
                                <tr key={i}>
                                    <td style={{ maxWidth: 200 }}>
                                        <div className="rv-response-snippet">{r.review_text.slice(0, 60)}{r.review_text.length > 60 ? '...' : ''}</div>
                                    </td>
                                    <td>
                                        <GlassBadge variant={entries[i]?.rating >= 4 ? 'success' : entries[i]?.rating === 3 ? 'warning' : 'danger'} size="sm">
                                            {entries[i]?.rating ?? '?'} ★
                                        </GlassBadge>
                                    </td>
                                    <td>{r.tone}</td>
                                    <td>
                                        {expandedRows.has(i) ? (
                                            <div style={{ whiteSpace: 'pre-wrap' }}>{r.response_text}</div>
                                        ) : (
                                            <div className="rv-response-snippet">{r.response_text.slice(0, 80)}{r.response_text.length > 80 ? '...' : ''}</div>
                                        )}
                                        <button className="rv-response-expand-btn" onClick={() => toggleExpand(i)}>
                                            {expandedRows.has(i) ? 'collapse' : 'expand'}
                                        </button>
                                    </td>
                                    <td><CopyButton text={r.response_text} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </GlassCard>
            )}
        </>
    );
};

// ---------------------------------------------------------------------------
// Tab 4: Saved Responses
// ---------------------------------------------------------------------------

const SavedResponsesTab: React.FC<{ projects: Project[] }> = ({ projects }) => {
    const [savedResponses, setSavedResponses] = useState<any[]>([]);
    const [selectedProject, setSelectedProject] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

    const projectOptions = [
        { value: '', label: 'Select a project...' },
        ...projects.map(p => ({ value: p.project_id, label: p.name })),
    ];

    const loadSaved = useCallback(async (projectId: string) => {
        if (!projectId) { setSavedResponses([]); return; }
        setIsLoading(true);
        setError(null);
        try {
            const data = await getSavedReviewResponses(projectId);
            setSavedResponses(data);
        } catch (e: any) {
            setError(getApiError(e, 'Failed to load saved responses'));
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadSaved(selectedProject);
    }, [selectedProject, loadSaved]);

    const handleDelete = async (responseId: string) => {
        if (!window.confirm('Delete this saved response?')) return;
        try {
            await deleteSavedReviewResponse(responseId);
            setSavedResponses(prev => prev.filter(r => r.response_id !== responseId));
        } catch (e: any) {
            setError(getApiError(e, 'Failed to delete'));
        }
    };

    const toggleExpand = (id: string) => setExpandedRows(prev => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });

    const PLATFORM_LABELS: Record<string, string> = { google: 'Google', facebook: 'Facebook', other: 'Other' };

    return (
        <GlassCard className="rv-form-card">
            <div className="rv-section-header">
                <h3>Saved Responses</h3>
            </div>
            <div className="rv-form-group">
                <label>Project</label>
                <GlassSelect options={projectOptions} value={selectedProject} onChange={setSelectedProject} placeholder="Select a project..." />
            </div>

            {error && <ErrorState message={error} />}
            {isLoading && <LoadingState message="Loading saved responses..." />}
            {!isLoading && selectedProject && savedResponses.length === 0 && (
                <div className="rv-empty">No saved responses yet. Generate and save a response from the Response Generator tab.</div>
            )}

            {!isLoading && savedResponses.length > 0 && (
                <table className="rv-saved-table">
                    <thead>
                        <tr>
                            <th>Review</th>
                            <th>Rating</th>
                            <th>Platform</th>
                            <th>Response</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {savedResponses.map(r => (
                            <tr key={r.response_id}>
                                <td style={{ maxWidth: 160 }}>
                                    <div className="rv-response-snippet">{r.review_text.slice(0, 50)}{r.review_text.length > 50 ? '...' : ''}</div>
                                </td>
                                <td><RatingBadge rating={r.rating} /></td>
                                <td>
                                    <GlassBadge variant="default" size="sm">{PLATFORM_LABELS[r.platform] || r.platform}</GlassBadge>
                                </td>
                                <td>
                                    {expandedRows.has(r.response_id) ? (
                                        <div style={{ whiteSpace: 'pre-wrap', fontSize: 'var(--text-sm)' }}>{r.response_text}</div>
                                    ) : (
                                        <div className="rv-response-snippet">{r.response_text.slice(0, 80)}{r.response_text.length > 80 ? '...' : ''}</div>
                                    )}
                                    <button className="rv-response-expand-btn" onClick={() => toggleExpand(r.response_id)}>
                                        {expandedRows.has(r.response_id) ? 'collapse' : 'expand'}
                                    </button>
                                </td>
                                <td>
                                    <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                                        <CopyButton text={r.response_text} />
                                        <GlassButton variant="ghost" size="sm" icon={<Trash2 size={14} />} onClick={() => handleDelete(r.response_id)} />
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </GlassCard>
    );
};

// ---------------------------------------------------------------------------
// Main Module
// ---------------------------------------------------------------------------

const ReviewsModule: React.FC = () => {
    const ctx = useOutletContext<{ selectedProjectId?: string }>();
    const selectedProjectId = ctx?.selectedProjectId;

    const [activeTab, setActiveTab] = useState('request-templates');
    const [brandGuides, setBrandGuides] = useState<BrandGuide[]>([]);
    const [projects, setProjects] = useState<Project[]>([]);

    useEffect(() => {
        getBrandGuides(selectedProjectId).then(setBrandGuides).catch(() => {});
        getProjects().then(setProjects).catch(() => {});
    }, [selectedProjectId]);

    if (!selectedProjectId) {
        return (
            <div className="reviews-page">
                <ModuleHeader
                    title="Review & Reputation Management"
                    breadcrumb={['Reviews']}
                />
                <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-secondary)' }}>
                    <Star size={64} style={{ opacity: 0.4, marginBottom: '16px' }} />
                    <h3 style={{ color: 'var(--text)', marginBottom: '8px' }}>Select a project</h3>
                    <p>Choose a project from the sidebar to view its reviews</p>
                </div>
            </div>
        );
    }

    return (
        <div className="reviews-page">
            <ModuleHeader
                title="Review & Reputation Management"
                breadcrumb={['Reviews']}
            />
            <div className="reviews-tabs-row">
                <GlassTabs tabs={TABS} activeTab={activeTab} onChange={setActiveTab} />
            </div>

            {activeTab === 'request-templates' && <RequestTemplatesTab brandGuides={brandGuides} projects={projects} />}
            {activeTab === 'response-gen' && <ResponseGeneratorTab brandGuides={brandGuides} projects={projects} />}
            {activeTab === 'bulk' && <BulkResponsesTab brandGuides={brandGuides} />}
            {activeTab === 'saved' && <SavedResponsesTab projects={projects} />}
        </div>
    );
};

export default ReviewsModule;
